import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, ArrowRight, SkipForward } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { SERVICE_OPTIONS } from "@/data/content";

export default function WhatsAppModal() {
  const { waModal, closeWhatsApp, defaultNumber, buildWaUrl, submitWhatsAppLead, trackWhatsAppClick } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", service_required: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const service = form.service_required || waModal.service;
  const page = window.location.pathname.replace(/\//g, "") || "home";

  const COUNTRY_KEY_MAP = { canada: "canada", uk: "uk", "united kingdom": "uk", britain: "uk", pakistan: "pakistan", india: "india", malaysia: "malaysia" };
  const resolveCountryKey = () => waModal.countryKey || COUNTRY_KEY_MAP[(form.country || "").trim().toLowerCase()] || null;

  const openWa = (number, leadId) => {
    window.open(buildWaUrl(number, service, page, leadId), "_blank", "noopener");
    closeWhatsApp();
  };

  const handleContinue = async () => {
    setSubmitting(true);
    try {
      const res = await submitWhatsAppLead({
        ...form, service_required: service, service_key: waModal.serviceKey, country_key: resolveCountryKey(),
      });
      openWa(res.whatsapp_number || defaultNumber, res.lead_id);
    } catch {
      openWa(defaultNumber);
    } finally { setSubmitting(false); }
  };

  const handleSkip = async () => {
    try {
      const res = await trackWhatsAppClick({ service_required: service, country_key: resolveCountryKey() });
      openWa(res.whatsapp_number || defaultNumber);
    } catch {
      openWa(defaultNumber);
    }
  };

  return (
    <Dialog open={waModal.open} onOpenChange={(o) => !o && closeWhatsApp()}>
      <DialogContent data-testid="whatsapp-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-navy">
            <MessageCircle className="text-emerald-brand" /> Chat on WhatsApp
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Share a few details so we can assist you faster, or skip straight to WhatsApp.</p>
        <div className="space-y-3 mt-2">
          <input data-testid="wa-name" value={form.name} onChange={set("name")} placeholder="Name" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-brand" />
          <input data-testid="wa-email" value={form.email} onChange={set("email")} placeholder="Email" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-brand" />
          <input data-testid="wa-phone" value={form.phone} onChange={set("phone")} placeholder="Phone or WhatsApp" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-brand" />
          <input data-testid="wa-country" value={form.country} onChange={set("country")} placeholder="Country" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-brand" />
          <select data-testid="wa-service" value={form.service_required} onChange={set("service_required")} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-emerald-brand">
            <option value="">Service required{waModal.service ? ` (${waModal.service})` : ""}</option>
            {SERVICE_OPTIONS.map((s) => <option key={s.label} value={s.label}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button data-testid="whatsapp-modal-submit" disabled={submitting} onClick={handleContinue} className="flex-1 btn-gold rounded-full py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            Continue to WhatsApp <ArrowRight size={16} />
          </button>
          <button data-testid="whatsapp-modal-skip" onClick={handleSkip} className="flex-1 rounded-full py-3 font-semibold border-2 border-slate-200 text-slate-600 hover:border-slate-300 flex items-center justify-center gap-2">
            Skip and Open <SkipForward size={16} />
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 text-center">We never capture or store personal information without your consent.</p>
      </DialogContent>
    </Dialog>
  );
}
